import { ComponentProps, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Link } from 'wouter';

import { SingSetup, SongPreview as SongPreviewEntity } from '~/interfaces';
import { Skeleton } from '~/modules/elements/akui/skeleton';
import { useBackground } from '~/modules/elements/background-context';
import events from '~/modules/game-events/game-events';
import { useEventEffect } from '~/modules/game-events/hooks';
import useBackgroundMusic from '~/modules/hooks/use-background-music';
import useBaseUnitPx from '~/modules/hooks/use-base-unit-px';
import useBlockScroll from '~/modules/hooks/use-block-scroll';
import useInstantSongPreview from '~/modules/hooks/use-instant-song-preview';
import useSmoothNavigate, { buildUrl } from '~/modules/hooks/use-smooth-navigate';
import useViewportSize from '~/modules/hooks/use-viewport-size';
import { GenreLabelsContext, useGenreLabels } from '~/modules/songs/hooks/use-genre-label';
import { useSetlist } from '~/modules/songs/hooks/use-setlist';
import LayoutGame from '~/routes/layout-game';
import { Components } from '~/routes/sing-a-song/song-selection/components/custom-virtualization';
import FilterPanel from '~/routes/sing-a-song/song-selection/components/filters/filter-panel';
import QueueDock from '~/routes/sing-a-song/song-selection/components/queue/queue-dock';
import { SongCard } from '~/routes/sing-a-song/song-selection/components/song-card';
import { SongGroupHeader } from '~/routes/sing-a-song/song-selection/components/song-group-header';
import SongGroupsNavigation from '~/routes/sing-a-song/song-selection/components/song-groups-navigation';
import SongPreview from '~/routes/sing-a-song/song-selection/components/song-preview';
import Toolbar from '~/routes/sing-a-song/song-selection/components/toolbar';
import {
  VirtualizedList,
  VirtualizedListMethods,
} from '~/routes/sing-a-song/song-selection/components/virtualized-list';
import { isPlaylistNarrowing } from '~/routes/sing-a-song/song-selection/hooks/use-playlists';
import useRemoteSongSelectionMirror from '~/routes/sing-a-song/song-selection/hooks/use-remote-song-selection-mirror';
import { SongGroup } from '~/routes/sing-a-song/song-selection/hooks/use-song-list';
import { isFilterSet } from '~/routes/sing-a-song/song-selection/hooks/use-song-list-filter';
import useSongSelection from '~/routes/sing-a-song/song-selection/hooks/use-song-selection';
import useVisibleSongGroups from '~/routes/sing-a-song/song-selection/hooks/use-visible-song-groups';
import { getSongIdWithNew } from '~/routes/sing-a-song/song-selection/utils/get-song-id-with-new';
import { cn } from '~/utils/cn';

interface Props {
  onSongSelected: (songSetup: SingSetup & { song: SongPreviewEntity }) => void;
  preselectedSong: string | null;
  /** Reports the currently focused song as the user browses (used by online mode to share it). */
  onSongFocused?: (song: SongPreviewEntity | undefined) => void;
}

declare global {
  var __songList:
    | {
        scrollToSong: (songId: string) => boolean;
      }
    | undefined;
}

export default function SongSelection({ onSongSelected, preselectedSong, onSongFocused }: Props) {
  const setlist = useSetlist();
  const [additionalSong, setAdditionalSong] = useState<string | null>(preselectedSong);
  const list = useRef<VirtualizedListMethods | null>(null);
  const { width, handleResize } = useViewportSize();
  // The root font size `index.css` scales off the viewport - 16px at 1080p, 32px at 4K. Every
  // measurement below is a multiple of it rather than a raw pixel count, so a bigger screen gets a
  // bigger card rather than more of them: on a television the viewer is further away, and cards at
  // the 1080p pixel size read as a wall of thumbnails.
  const unit = useBaseUnitPx();
  const widthInUnits = width / unit;

  // Cards per row: the redesign's three through a 1080p screen, then one more per step up. A fixed
  // three left anything larger showing a 1440px column with the rest of the monitor empty either
  // side - 560px of it on a 1440p screen, a thousand on an ultrawide.
  //
  // Measured in units, so the step is about how much room there is *relative to the type*, not how
  // many pixels the panel has. The thresholds are the old pixel ones over a 16px root, so every
  // screen up to 1080p lays out exactly as before; above it a 4K television now draws the same four
  // across that a 1080p monitor does, at twice the size, instead of five small ones.
  const songsPerRow =
    widthInUnits < 40 ? 1 : widthInUnits < 45 ? 2 : widthInUnits < 120 ? 3 : widthInUnits < 160 ? 4 : 5;
  // Gap and side padding are each this share of the content width.
  const LIST_SPACING_RATIO = 0.02;
  // Cards stop growing here, so a very wide screen gets its margins back rather than cards the size
  // of a poster. In units for the same reason as the rest: the cap has to grow with the type, or it
  // becomes the thing that pins a 4K card back to its 1080p size.
  const MAX_SONG_ENTRY_WIDTH_UNITS = 40;
  // The content width at which this row's cards would hit that maximum. Solved for rather than added
  // up, because the two side paddings and the gap between each pair are all shares of that same
  // width: cards occupy `1 - ratio * (songsPerRow + 1)` of it.
  const maxContentWidth = Math.round(
    (MAX_SONG_ENTRY_WIDTH_UNITS * unit * songsPerRow) / (1 - LIST_SPACING_RATIO * (songsPerRow + 1)),
  );
  const effectiveWidth = Math.min(width, maxContentWidth);
  const LIST_GAP_PX = Math.max(8, Math.round(effectiveWidth * LIST_SPACING_RATIO));
  const LIST_PADDING_PX = Math.max(8, Math.round(effectiveWidth * LIST_SPACING_RATIO));

  useBackgroundMusic(false);
  useBackground(true);
  useBlockScroll();
  const {
    focusedSong,
    moveToSong,
    groupedSongList,
    keyboardControl,
    toolbarFocusMode,
    queueDockActive,
    exitQueueDock,
    filtersOpen,
    openFilters,
    closeFilters,
    row1Register,
    row2Register,
    songPreview,
    setKeyboardControl,
    setFilters,
    filters,
    showFilters,
    isLoading,
    unverifiedSongsLoading,
    randomSong,
    selectedPlaylist,
    setSelectedPlaylist,
    playlists,
    songList,
    catalogue,
  } = useSongSelection(additionalSong, songsPerRow);
  // Read off the whole catalogue rather than the filtered list, so a card's genre reads the same
  // whichever filter is on - and computed once here rather than per card, which would be a pass
  // over six thousand songs for every thumbnail on screen.
  const genreLabels = useGenreLabels(catalogue);
  // The dot on the filter icon. The search says so on its own, so it is not part of this; the
  // playlist is, because picking Eurovision or Duets narrows the list exactly the way the panel's
  // own categories do - all but the views the screen opens on, see `isPlaylistNarrowing`.
  const filtersActive =
    isFilterSet(filters.genre) ||
    isFilterSet(filters.language) ||
    isFilterSet(filters.duet) ||
    isFilterSet(filters.played) ||
    isFilterSet(filters.length) ||
    isPlaylistNarrowing(selectedPlaylist);
  const songPreviewInGroup = useMemo(
    () =>
      groupedSongList
        .map((group) => group.songs)
        .flat()
        .find((song) => song.song.id === songPreview?.id),
    [songPreview, groupedSongList],
  );

  useEffect(() => {
    onSongFocused?.(songPreview);
  }, [songPreview, onSongFocused]);

  const selectedPlaylistData = useMemo(
    () => playlists.find((playlist) => playlist.name === selectedPlaylist),
    [playlists, selectedPlaylist],
  );

  const listWidth = effectiveWidth - LIST_PADDING_PX * 2;
  const songEntryWidth = (listWidth - (songsPerRow - 1) * LIST_GAP_PX) / songsPerRow;
  // Portrait card: 16:9 thumbnail on top + a text section below. In units, not pixels: the title,
  // artist and badges inside it are all `rem`, so a fixed 120px strip held its 1080p height while
  // the text in it doubled on a 4K screen - which is what cut the song titles off halfway down.
  const CARD_METADATA_HEIGHT_UNITS = 7.5;
  const songEntryHeight = songEntryWidth * (9 / 16) + CARD_METADATA_HEIGHT_UNITS * unit;
  const songGroupHeight = songEntryHeight / 2.5;

  const expandSong = useCallback(() => setKeyboardControl(false), [setKeyboardControl]);
  const instantSongPreview = useInstantSongPreview();
  const selectAndExpandSong = useCallback(
    (songId: string) => {
      moveToSong(songId);
      expandSong();
    },
    [moveToSong, expandSong],
  );

  /**
   * Swaps the open card over to another song, leaving it open. A sibling from the series lane need
   * not have survived the filters the list is showing, so one that has not is let through as the
   * additional song — the same way a song picked on a phone reaches a list it was filtered out of.
   */
  const selectSongInPreview = useCallback(
    (songId: string) => {
      if (songList.some((song) => song.id === songId)) moveToSong(songId);
      else setAdditionalSong(songId);
    },
    [songList, moveToSong],
  );

  const loading = isLoading || unverifiedSongsLoading || !groupedSongList || !width;
  const forceFlag = selectedPlaylist === 'Eurovision';

  const container = useRef<HTMLDivElement>(null);
  const header = useRef<HTMLDivElement>(null);
  // The fixed header is a search row and the letter row, so it is well over the 80px that used to be
  // hard-coded here - the first rows of songs sat underneath it. Measured rather than guessed,
  // because its height moves with the viewport and with how far the letter row wraps.
  const [headerHeight, setHeaderHeight] = useState(80);

  useLayoutEffect(() => {
    const element = header.current;
    if (!element) return;

    const measure = () => setHeaderHeight(element.offsetHeight);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);
  // Measured rather than assumed: the dock's height follows the card lane, which follows the
  // viewport, and the last row of songs has to stay reachable above it.
  const [queueDockHeight, setQueueDockHeight] = useState(0);

  // The nav row is empty while loading, and the on-screen row, the visibility observer and the
  // remote-mic mirror all have to agree on which groups exist — so the swap happens once, here.
  const navGroups = loading ? [] : groupedSongList;
  const visibleGroups = useVisibleSongGroups(container, navGroups);

  const scrollToGroup = useCallback(
    (group: SongGroup) => {
      if (group.songs.length === 0) return;

      moveToSong(getSongIdWithNew(group.songs[0], group));
      // wait for the song to be selected and scrolled into view - then override the scroll and scroll to the group instead
      setTimeout(() => list.current?.scrollToGroup(group.name), 20);
    },
    [moveToSong],
  );

  useRemoteSongSelectionMirror({
    playlists,
    catalogue,
    selectedPlaylist,
    groupedSongList: navGroups,
    visibleGroups,
    focusedSong,
  });

  useEventEffect(events.remotePlaylistSelected, (name) => setSelectedPlaylist(name), [setSelectedPlaylist]);

  useEventEffect(
    events.remoteSongGroupSelected,
    (name) => {
      const group = navGroups.find((candidate) => candidate.name === name);
      if (group) scrollToGroup(group);
    },
    [navGroups, scrollToGroup],
  );

  // API for Playwright as with virtualization it's super tricky to test
  useEffect(() => {
    global.__songList = {
      scrollToSong: (songId: string) => {
        // If the song is in a new group, we need to remove the '-new-group' suffix
        const [cleanSongId] = songId.split('-new-group');
        const isNewGroup = songId.endsWith('-new-group');
        const songGroup = groupedSongList.find(
          (group) => !!group.isNew === isNewGroup && group.songs.some((song) => song.song.id === cleanSongId),
        );

        if (songGroup) {
          list.current?.scrollToSongInGroup(songId, 'instant');
          return true;
        }
        return false;
      },
    };

    return () => {
      delete global.__songList;
    };
  }, [groupedSongList]);

  const [{ previewTop, previewLeft }, setPreviewPosition] = useState(() => ({
    previewTop: 0,
    previewLeft: 0,
  }));
  useEffect(() => {
    const song = document.querySelector<HTMLDivElement>(`[data-song-id="${focusedSong}"]`);
    if (!isLoading && song) {
      const position = list.current?.getSongPosition(focusedSong);
      setPreviewPosition({
        previewTop: position?.y ?? 0,
        previewLeft: song.offsetLeft,
      });
      list.current?.scrollToSongInGroup(focusedSong);
    } else if (!isLoading) {
      console.warn(`!!!!!!!!!!!! Song not found "${focusedSong}" !!!!!!!!!!!!`, focusedSong, 'focusedSong');
    }
  }, [focusedSong, isLoading, width, songList]);

  useLayoutEffect(() => {
    handleResize(); // Recalculate width/height to account possible scrollbar appearing
    if (!isLoading) {
      list.current?.scrollToSongInGroup(focusedSong, 'auto');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recalculates layout on width/playlist/search changes; `focusedSong` is read latest and has its own scroll effect above
  }, [width, selectedPlaylist, filters.search, isLoading, handleResize]);

  const navigate = useSmoothNavigate();
  useHotkeys(
    'Shift + E',
    () => {
      if (document.fullscreenElement !== null) {
        document.exitFullscreen().catch(console.warn);
      }
      if (songPreview?.isUnverifiedSong && songPreview.sharedSongId) {
        navigate(
          buildUrl(`edit/song/`, {
            playlist: null,
            step: 'sync',
            song: null,
            externalSong: songPreview.sharedSongId,
          }),
        );
      } else {
        navigate(
          buildUrl(`edit/song/`, {
            playlist: null,
            step: 'metadata',
            externalSong: null,
          }),
        );
      }
    },
    [navigate, songPreview],
  );

  useEventEffect(
    events.remoteSongSelected,
    async (songId) => {
      const isSongOnTheList = songList.some((song) => song.id === songId);
      if (!isSongOnTheList) {
        return setKeyboardControl(false, () => {
          setAdditionalSong(songId);
        });
      }
      if (focusedSong !== songId) {
        moveToSong(songId);
        if (!keyboardControl) {
          setKeyboardControl(true);
        }
        expandSong();
      } else if (keyboardControl) {
        expandSong();
      }
    },
    [songList, focusedSong, keyboardControl, expandSong, moveToSong],
  );

  if (keyboardControl && additionalSong && additionalSong !== focusedSong && !isLoading) {
    setAdditionalSong(null);
  }

  return (
    <GenreLabelsContext.Provider value={genreLabels}>
      <LayoutGame>
        <QueueDock
          active={queueDockActive}
          onExit={exitQueueDock}
          collapsed={!keyboardControl}
          onHeightChange={setQueueDockHeight}
        />
        <FilterPanel
          open={filtersOpen}
          onClose={closeFilters}
          catalogue={catalogue}
          resultCount={songList.length}
          filters={filters}
          setFilters={setFilters}
          playlists={playlists}
          selectedPlaylist={selectedPlaylist}
          setSelectedPlaylist={setSelectedPlaylist}
        />
        <div
          className="flex h-screen flex-col overflow-hidden"
          style={
            {
              '--song-group-header-height': `${Math.floor(songGroupHeight)}px`,
              '--song-entry-width': `${Math.floor(songEntryWidth)}px`,
              '--song-entry-height': `${Math.floor(songEntryHeight)}px`,
              '--song-list-gap': `${LIST_GAP_PX}px`,
              '--song-list-padding-left': `${LIST_PADDING_PX}px`,
              '--song-list-padding-right': `${LIST_PADDING_PX}px`,
              '--song-list-max-width': `${effectiveWidth}px`,
            } as React.CSSProperties
          }>
          <div
            ref={header}
            className="fixed top-0 right-0 left-0 z-100 flex flex-col border-b border-white/10 bg-slate-950/50 pt-5 pb-2 backdrop-blur-md">
            <div className="mx-auto flex w-full max-w-(--song-list-max-width) flex-col pr-(--song-list-padding-right) pl-(--song-list-padding-left)">
              {/* One width for the search row, the count and the letter row. The right margin
                  clears the fixed help and fullscreen buttons in the top-right corner, and it is on
                  the container rather than on the search row because a row that ends short of the
                  row above it reads as a different, narrower thing.
                  Kept at every width: it used to drop to zero from 1760px up, which was safe only
                  while the content stopped at 1440px and those buttons sat outside it. Now that the
                  row fills the screen it would run underneath them. */}
              <div className="mr-25 flex flex-col gap-2 md:mr-41">
                <Toolbar
                  filters={filters}
                  setFilters={setFilters}
                  onRandom={randomSong}
                  onOpenFilters={openFilters}
                  filtersActive={filtersActive}
                  keyboardControl={keyboardControl}
                  keyboardNavRegister={row1Register}
                  toolbarNavActive={toolbarFocusMode}
                />
                {/* No running count between the search row and the letters. "How much is left after
                    the filters" is a question about the filters, and the filter panel answers it in
                    `filters-result-count`; standing here it was a number on every screen of a
                    browsing session that only means something during one. */}
                <SongGroupsNavigation
                  groupedSongList={navGroups}
                  visibleGroups={visibleGroups}
                  keyboardNavRegister={row2Register}
                  focusedSong={focusedSong}
                  onScrollToGroup={scrollToGroup}
                />
              </div>
            </div>
          </div>
          <div
            className={`relative flex-1 overflow-auto transition-opacity duration-500 ${showFilters || toolbarFocusMode || queueDockActive ? 'opacity-50' : ''}`}
            data-test="song-list-container"
            style={{ paddingBottom: queueDockHeight }}
            ref={container}>
            {loading ? (
              <div style={{ paddingTop: headerHeight }}>
                <GroupRow>
                  <SongGroupHeader />
                </GroupRow>
                {new Array(unverifiedSongsLoading ? 1 : 4).fill(0).map((_, i) => (
                  <ListRow key={i}>
                    {new Array(songsPerRow).fill(0).map((_, j) => (
                      <Skeleton
                        key={j}
                        className="flex-none rounded-2xl bg-black/45"
                        style={{
                          flexBasis: 'var(--song-entry-width)',
                          width: 'var(--song-entry-width)',
                          height: 'var(--song-entry-height)',
                        }}
                      />
                    ))}
                  </ListRow>
                ))}
              </div>
            ) : (
              <VirtualizedList
                focusedSong={focusedSong}
                topPadding={headerHeight}
                ListRowWrapper={ListRow}
                GroupRowWrapper={GroupRow}
                ref={list}
                groups={groupedSongList}
                itemHeight={Math.floor(songEntryHeight) + LIST_GAP_PX}
                groupHeight={Math.floor(songGroupHeight) + LIST_GAP_PX}
                components={components}
                renderGroup={(group) => <SongGroupHeader key={group.name} group={group} />}
                perRow={songsPerRow}
                renderItem={(songItem, group) => {
                  const songId = getSongIdWithNew(songItem, group);
                  const isFocused = songId === focusedSong;
                  return (
                    <SongListEntry
                      {...(showFilters || !keyboardControl ? { 'data-unfocusable': true } : {})}
                      isPopular={songItem.isPopular}
                      key={songItem.song.id}
                      song={songItem.song}
                      handleClick={isFocused ? expandSong : instantSongPreview ? selectAndExpandSong : moveToSong}
                      focused={!showFilters && keyboardControl && isFocused}
                      songId={songId}
                      groupLetter={group.name}
                      data-song-index={songItem.index}
                      data-song-id={songId}
                      data-focused={!showFilters && keyboardControl && isFocused}
                      data-test={`song-${getSongIdWithNew(songItem, group)}`}
                      data-group={group.name}
                      forceFlag={forceFlag}
                    />
                  );
                }}
                placeholder={
                  <div
                    className="invisible flex-none rounded-2xl"
                    style={{
                      flexBasis: 'var(--song-entry-width)',
                      width: 'var(--song-entry-width)',
                      height: 'var(--song-entry-height)',
                    }}
                  />
                }
                context={{
                  songPreviewProps: {
                    forceFlag,
                    isPopular: !!songPreviewInGroup?.isPopular,
                    keyboardControl: !keyboardControl,
                    onPlay: onSongSelected,
                    onExitKeyboardControl: () => setKeyboardControl(true),
                    onExpand: expandSong,
                    onSelectSong: selectSongInPreview,
                    songPreview,
                    top: previewTop,
                    left: previewLeft,
                    width: Math.floor(songEntryWidth),
                    height: Math.floor(songEntryHeight),
                  },
                }}
                Footer={
                  selectedPlaylistData?.footerComponent ??
                  (setlist.isEditable ? (
                    <div className="typography mt-auto pt-20 text-center text-lg text-white sm:text-xl">
                      Missing a song? Try{' '}
                      <Link to="convert/">
                        <a>adding one</a>
                      </Link>{' '}
                      yourself!
                    </div>
                  ) : null)
                }
              />
            )}
          </div>
        </div>
      </LayoutGame>
    </GenreLabelsContext.Provider>
  );
}

const ListRow = ({ children, ...props }: ComponentProps<'div'>) => (
  <div {...props} className={`relative mb-(--song-list-gap) ${props.className ?? ''}`}>
    <div className="mx-auto flex max-w-(--song-list-max-width) flex-nowrap gap-(--song-list-gap) pr-(--song-list-padding-right) pl-(--song-list-padding-left)">
      {children}
    </div>
  </div>
);

const GroupRow = ({ children, ...props }: ComponentProps<'div'>) => (
  <div {...props} className={`mb-(--song-list-gap) ${props.className ?? ''}`}>
    <div className="mx-auto flex max-w-(--song-list-max-width) flex-nowrap gap-(--song-list-gap) pr-(--song-list-padding-right) pl-(--song-list-padding-left)">
      {children}
    </div>
  </div>
);

interface SongListEntryProps extends ComponentProps<typeof SongCard> {
  focused: boolean;
  songId?: string;
  groupLetter?: string;
  handleClick?: (songId: string, groupLetter?: string) => void;
}

const SongListEntry = memo(({ focused: isFocused, songId, groupLetter, handleClick, ...props }: SongListEntryProps) => {
  const onClickCallback = useCallback(
    () => (handleClick ? handleClick(songId!, groupLetter) : undefined),
    [handleClick, songId, groupLetter],
  );

  return (
    <SongCard
      {...props}
      focused={isFocused}
      onClick={handleClick ? onClickCallback : undefined}
      className={cn(
        'flex-none cursor-pointer transition-all duration-300',
        isFocused ? 'z-2' : 'hover:border-white/20',
        props.className,
      )}
      style={{
        flexBasis: 'var(--song-entry-width)',
        width: 'var(--song-entry-width)',
        height: 'var(--song-entry-height)',
        ...props.style,
      }}>
      <SongCard.Thumbnail>
        <SongCard.Thumbnail.Genre />
        <SongCard.Thumbnail.Length />
      </SongCard.Thumbnail>
      <SongCard.Footer>
        <SongCard.SongTitle />
        <SongCard.Artist />
        <SongCard.Badges>
          <SongCard.Badges.Flag />
          <SongCard.Badges.Duet />
          <SongCard.Badges.Stats compact />
        </SongCard.Badges>
      </SongCard.Footer>
    </SongCard>
  );
});
SongListEntry.displayName = 'SongListEntry';

const components: Components<{
  songPreviewProps: Omit<ComponentProps<typeof SongPreview>, 'songPreview'> & { songPreview?: SongPreviewEntity };
}> = {
  Header: ({ context }) => (
    <>
      <div />
      {context?.songPreviewProps.songPreview && (
        <SongPreview {...context.songPreviewProps} songPreview={context.songPreviewProps.songPreview} />
      )}
    </>
  ),
  EmptyPlaceholder: () => (
    <div className="typography flex h-[30vh] flex-1 items-center justify-center text-xl text-white sm:text-4xl">
      No songs found
    </div>
  ),
};
